import { useEffect, useState, useContext } from 'react';

import { useHeight } from '../hooks';
import { useAdditionalFetchDataHelpersPluginCallback } from '../../AdditionalFetchDataHelpersPlugin';
import { MediaEmpty } from '../../../../../MediaEmpty';
import { Box } from '../../../../../Box';
import { Typography } from '../../../../../Typography';
import { Skeleton } from '../../../../../Skeleton';
import { WysiwygContext } from '../../../context';
import { useLogger } from '../../../../../services/hooks';

import { ImagesGallerySlider } from './ImagesGallerySlider';

interface ImagesGalleryProps {
  images: { name: string; id: string }[];
  description?: string;
  editable: boolean;
}

export const ImagesGallery = ({
  images,
  description,
  editable,
}: ImagesGalleryProps) => {
  const { maxHeight, editorHeight } = useHeight();
  const contextHelpers = useAdditionalFetchDataHelpersPluginCallback();
  const { apiUrl } = useContext(WysiwygContext);
  const logger = useLogger();

  const height = editable ? editorHeight : maxHeight;

  const [formatedImages, setFormatedImages] = useState<
    {
      src: string;
      name: string;
      id: string;
    }[]
  >([]);
  const [failedImageCounter, setFailedImageCounter] = useState(0);

  const hasError = failedImageCounter === images.length;

  useEffect(() => {
    if (formatedImages.length) return;

    if (contextHelpers?.getAttachmentUrl) {
      let failedImages = 0;
      const imagesList = images.map(async (image) => {
        const imageSrc = await contextHelpers
          .getAttachmentUrl(image.id)
          .then((res) => {
            return res;
          })
          .catch((err) => {
            logger.error(err, {
              tags: {
                vkdoc_error_type: 'wysiwyg input image gallery',
              },
            });

            failedImages += 1;
            return '';
          });

        return {
          ...image,
          src: imageSrc,
        };
      });

      Promise.all(imagesList).then((result) => {
        setFormatedImages(() => result);
        setFailedImageCounter(() => failedImages);
      });
    }
  }, [formatedImages, images, contextHelpers, apiUrl, logger]);

  if (!formatedImages.length) {
    return <Skeleton height={height} width="100%" variant="rect" />;
  }

  if (failedImageCounter === images.length) {
    return <MediaEmpty height={height} variant="multiple" radius="m" />;
  }

  return (
    <Box display="flex" flexDirection="column" gap="12">
      <ImagesGallerySlider
        images={formatedImages}
        hasError={hasError}
        editable={editable}
      />

      {description && (
        <Typography variant="caption" color="text.light.tertirary">
          {description}
        </Typography>
      )}
    </Box>
  );
};
